import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { colors } from '../../const/colors'
import { IReservationVariant } from '../../graphql/parkingPool/getParkingPoolAvailability'
import { StepTitle, SectionLabel, ErrorBanner } from './reserveOnBehalfUi'
import {
  isSingleHall,
  mixedRows,
  singleHallName,
} from './reservationVariantsPicker.utils'

const Radio: FC<{ selected: boolean }> = ({ selected }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...(selected
        ? { background: colors.green }
        : { border: `2px solid ${colors.borderDash}`, background: '#fff' }),
    }}
  >
    {selected && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
  </Box>
)

const variantCardSx = (selected: boolean) => ({
  borderRadius: '15px',
  p: '15px 16px',
  cursor: 'pointer',
  ...(selected
    ? { border: `2px solid ${colors.green}`, background: '#F1F7F1' }
    : { border: `1.5px solid ${colors.border}`, background: '#fff' }),
})

const ReservationVariantsPicker: FC<{
  selectedCount: number
  variants: IReservationVariant[]
  loading: boolean
  error?: string | null
  variantIdx: number | null
  onSelect: (idx: number) => void
  noVariantsReason?: string | null
}> = ({
  selectedCount,
  variants,
  loading,
  error,
  variantIdx,
  onSelect,
  noVariantsReason,
}) => {
  const { t } = useTranslation(['user_details'])
  const dlabel = t('reserve_modal.days', { count: selectedCount || 1 })

  return (
    <Box>
      <StepTitle
        n="2"
        title={t('reserve_modal.step2_title')}
        sub={t('reserve_modal.step2_sub')}
      />

      {!selectedCount ? (
        <Typography sx={{ fontSize: '13px', color: colors.slate }}>
          {t('reserve_modal.select_days_first')}
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CircularProgress size={18} sx={{ color: colors.navy }} />
          <Typography sx={{ fontSize: '13px', color: colors.slate }}>
            {t('reserve_modal.loading_variants')}
          </Typography>
        </Box>
      ) : error ? (
        <ErrorBanner
          heading={t('reserve_modal.error_heading')}
          message={error}
        />
      ) : !variants.length ? (
        <Box data-e2e="no-variants">
          <Typography sx={{ fontSize: '13px', color: colors.slate }}>
            {t('reserve_modal.no_variants')}
          </Typography>
          {noVariantsReason && (
            <Typography
              sx={{ fontSize: '13px', color: colors.slate, mt: '4px' }}
            >
              {noVariantsReason}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {variants.some(isSingleHall) && (
            <>
              <SectionLabel>{t('reserve_modal.single_hall')}</SectionLabel>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                {variants.map((v, idx) =>
                  isSingleHall(v) ? (
                    <Box
                      key={idx}
                      onClick={() => onSelect(idx)}
                      data-e2e="reservation-variant"
                      sx={variantCardSx(variantIdx === idx)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '13px',
                        }}
                      >
                        <Radio selected={variantIdx === idx} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '14.5px',
                              fontWeight: 800,
                              color: colors.navy,
                            }}
                          >
                            {singleHallName(v)}
                          </Typography>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            background: colors.greenBg,
                            color: colors.greenBgText,
                            fontSize: '11.5px',
                            fontWeight: 700,
                            p: '5px 11px',
                            borderRadius: '9px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {t('reserve_modal.both_slots', { days: dlabel })}
                        </Box>
                      </Box>
                    </Box>
                  ) : null,
                )}
              </Box>
            </>
          )}

          {variants.some(v => !isSingleHall(v)) && (
            <>
              <SectionLabel sx={{ mt: '18px' }}>
                {t('reserve_modal.mixed_variants')}
              </SectionLabel>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                {variants.map((v, idx) =>
                  !isSingleHall(v) ? (
                    <Box
                      key={idx}
                      onClick={() => onSelect(idx)}
                      data-e2e="reservation-variant"
                      sx={variantCardSx(variantIdx === idx)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '13px',
                        }}
                      >
                        <Radio selected={variantIdx === idx} />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '9px',
                          }}
                        >
                          {mixedRows(v).map((rw, ri) => (
                            <Box
                              key={ri}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 700,
                                  color: colors.navy,
                                }}
                              >
                                {rw.name}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  background: colors.greenBg,
                                  color: colors.greenBgText,
                                  fontSize: '11.5px',
                                  fontWeight: 700,
                                  p: '4px 10px',
                                  borderRadius: '8px',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                }}
                              >
                                {rw.label}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : null,
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default ReservationVariantsPicker
